import { useEffect, useState } from 'react'
import { AppState, AppStateStatus } from 'react-native'
import { useIsFocused } from '@react-navigation/native'

// `currentState` can be null for an instant at launch: the app is then in front.
const isActive = (state: AppStateStatus | null | undefined): boolean =>
  state == null || state === 'active'

/**
 * True while the screen is really in front of the user: its route is focused
 * AND the app is in the foreground (on web: the browser tab is visible).
 *
 * Used to refresh a screen by itself only while someone is looking at it, so
 * nothing is asked from the server for a hidden tab or a backgrounded app.
 */
export const useIsScreenLive = (): boolean => {
  const isFocused = useIsFocused()
  const [appActive, setAppActive] = useState(() => isActive(AppState.currentState))

  useEffect(() => {
    // The state may have changed between the first render and this effect.
    setAppActive(isActive(AppState.currentState))
    const subscription = AppState.addEventListener('change', (state) => {
      setAppActive(isActive(state))
    })
    return () => subscription.remove()
  }, [])

  return isFocused && appActive
}
